/**
 * AOP 2027 Planning — the Approvals tab (David, Oct 29 2026).
 *
 * One place to review every department: its state with the same actions as on its own tab
 * (`AopDepartmentStatus`: submit for the head, ticks, approve, reopen, sync), and a table of its
 * drafts with the rationale written out — on the tree it is only a tooltip. Departments waiting for
 * review come first, approved ones last. Only for the reviewers and Anna/David: the page shows the
 * tab to them, and the proxy (`GET /approvals`) refuses everyone else.
 *
 * Single roles (David, Oct 29 2026): on a SUBMITTED department each role has "Revisit in next
 * iteration" — "Decline" in the last iteration ("Iteration III", `isFinalIteration`); the same
 * action either way. With a note, it leaves the plan while the rest stays submitted with its ticks,
 * so the department is then approved without it (`declineDraft` in `proxy-server/lib/aopReviews.js`).
 * Such roles are KEPT (`AOP [declined]`) and listed under the table across iterations as "set aside"
 * (or "declined" in the last iteration); Anna and David can restore one.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import {
  AOP_APPROVALS_QUERY_KEY,
  AOP_TIMELINE_QUERY_KEY,
  type AopApprovalDraft,
  type AopDeclinedRole,
  type AopIterationStatus,
  declineAopDraft,
  fetchAopApprovals,
  restoreAopDraft,
} from '../../services/aopPlanningApi';
import { formatEuro } from '../../types/compensation';
import { formatDay } from '../internalPlayground/backfillForm';
import { AopDepartmentStatus } from './AopIterationBar';
import { AopEditModal } from './AopEditModal';
import type { OrgNode } from '../../services/internalPlaygroundApi';
import { isFinalIteration, personName } from './aopFormat';

const MAX_NOTE = 1000;

type Entry = AopIterationStatus['departments'][string] | undefined;

/** Review order: waiting for review, then back with the head, then not submitted, approved last. */
export function reviewRank(entry: Entry): number {
  if (entry?.state === 'submitted') return 0;
  if (entry?.state === 'returned' || entry?.state === 'withdrawn') return 1;
  if (!entry) return 2;
  return 3;
}

/** The tab — see the module note. `status` is the page's iteration block, so actions refresh it. */
export function AopApprovalsTab({ status, root }: { status: AopIterationStatus; root: OrgNode }) {
  const { data, isPending, error } = useQuery({
    queryKey: AOP_APPROVALS_QUERY_KEY,
    queryFn: fetchAopApprovals,
    staleTime: 60 * 1000,
  });

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" aria-label="Loading the approvals" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div role="alert" className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
        <p>{error instanceof Error ? error.message : 'The approvals could not be loaded.'}</p>
      </div>
    );
  }

  // Off the page (renamed, or declined roles with no review on record): last, and never counted.
  const departments = [...data.departments].sort(
    (a, b) =>
      Number(a.onPage === false) - Number(b.onPage === false) ||
      reviewRank(status.departments[a.department]) - reviewRank(status.departments[b.department]) ||
      a.department.localeCompare(b.department)
  );
  const count = (rank: number) =>
    departments.filter((d) => d.onPage !== false && reviewRank(status.departments[d.department]) === rank).length;

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-600">
        {count(0)} to review · {count(1)} back with the head · {count(2)} not submitted · {count(3)} approved
      </p>
      {data.declinedUnavailable && (
        <p role="alert" className="text-sm text-amber-700">
          The declined roles could not be loaded right now. The plans below are complete; refresh in a moment.
        </p>
      )}
      {departments.map(({ department, onPage = true, drafts, declined = [] }) => {
        const entry = status.departments[department];
        const canRestore = Boolean(status.canApprove);
        if (!onPage) {
          return (
            <section key={department} aria-label={department} className="space-y-2 rounded-md border border-zinc-200 bg-white p-3 text-sm">
              <h2 className="text-base font-semibold text-zinc-900">{department}</h2>
              <DeclinedList department={department} declined={declined} canRestore={canRestore} />
            </section>
          );
        }
        // The proxy's rule: a submitted department, a reviewer or Anna/David, in an open iteration.
        const reviewing = Boolean(status.reviewerKey || status.canApprove) && Boolean(status.current);
        const canDecline = reviewing && entry?.state === 'submitted';
        // Edit (David, Oct 29 2026): any department not approved — a submitted one stays submitted.
        const canEdit = reviewing && entry?.state !== 'approved';
        return (
          <section key={department} aria-label={department} className="space-y-2 rounded-md border border-zinc-200 bg-white p-3 text-sm">
            <h2 className="text-base font-semibold text-zinc-900">
              {department}
              <span className="font-normal text-zinc-500"> · {draftSummary(drafts)}</span>
            </h2>
            {status.current ? (
              <AopDepartmentStatus status={status} department={department} />
            ) : (
              <p className="text-zinc-500">No iteration is open yet.</p>
            )}
            <DraftsTable
              drafts={drafts}
              department={department}
              canDecline={canDecline}
              canEdit={canEdit}
              root={root}
              final={isFinalIteration(status.current?.name)}
            />
            {declined.length > 0 && (
              <DeclinedList department={department} declined={declined} canRestore={canRestore} />
            )}
          </section>
        );
      })}
    </div>
  );
}

/**
 * The department's declined roles, kept for later iterations: who declined each, in which
 * iteration and why, and — for Anna and David — "Restore", which puts it back into the plan
 * (withdrawing a submitted department, refused for an approved one; the proxy's words if so).
 */
function DeclinedList({ department, declined, canRestore }: { department: string; declined: AopDeclinedRole[]; canRestore: boolean }) {
  const queryClient = useQueryClient();
  const restore = useMutation({
    mutationFn: (recordId: string) => restoreAopDraft(recordId),
    // The timeline key covers the approvals list too: the role reappears among the drafts.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: AOP_TIMELINE_QUERY_KEY }),
  });
  return (
    <div className="space-y-1 border-t border-zinc-100 pt-2 text-xs text-zinc-500">
      {/* Neutral: the list mixes roles set aside to revisit (they come back when the next iteration
          opens), roles declined in the last iteration, and any that did not come back. */}
      <p className="font-medium text-zinc-600">Set aside or declined</p>
      <ul role="list" aria-label={`Set aside in ${department}`} className="m-0 list-none space-y-1 p-0">
        {declined.map((d) => (
          <li key={d.recordId} className="flex flex-wrap items-center gap-2">
            <span className="text-zinc-700">{d.title}</span>
            <span>
              {[d.role, d.trackAndLevel, d.plannedStartDate ? formatDay(d.plannedStartDate) : null].filter(Boolean).join(' · ')}
            </span>
            <span>
              {/* "declined" only for what the last iteration declined; before, it was set aside to revisit. */}
              — {d.declinedBy
                ? `${isFinalIteration(d.iterationName) ? 'declined' : 'set aside'}${d.iterationName ? ` in ${d.iterationName}` : ''} by ${personName(d.declinedBy)}`
                : 'set aside (no review on record)'}
              {d.note ? `: “${d.note}”` : ''}
            </span>
            {canRestore && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="ml-auto"
                aria-label={`Restore ${d.title}`}
                disabled={restore.isPending}
                loading={restore.isPending && restore.variables === d.recordId}
                onClick={() => !restore.isPending && restore.mutate(d.recordId)}
              >
                Restore
              </Button>
            )}
          </li>
        ))}
      </ul>
      {restore.error && (
        <p role="alert" className="text-red-700">
          {(restore.error as Error).message}
        </p>
      )}
    </div>
  );
}

/** "3 drafts · €180.000 a year" — and how many have no figure, so the sum is never read as whole. */
function draftSummary(drafts: AopApprovalDraft[]): string {
  if (!drafts.length) return 'no drafts';
  const priced = drafts.filter((d) => d.yearlyCost != null);
  const sum = priced.reduce((total, d) => total + (d.yearlyCost ?? 0), 0);
  const missing = drafts.length - priced.length;
  return (
    `${drafts.length} ${drafts.length === 1 ? 'draft' : 'drafts'}` +
    (priced.length ? ` · ${formatEuro(sum)} a year` : '') +
    (missing ? ` (${missing} without a figure)` : '')
  );
}

/** The department's drafts, one row each, the rationale in full; "Revisit in next iteration" (or, in
 * the last iteration, "Decline") per row where allowed. */
function DraftsTable({
  drafts,
  department,
  canDecline,
  canEdit,
  root,
  final,
}: {
  drafts: AopApprovalDraft[];
  department: string;
  canDecline: boolean;
  canEdit: boolean;
  root: OrgNode;
  final: boolean;
}) {
  const [declining, setDeclining] = useState<AopApprovalDraft | null>(null);
  const [editing, setEditing] = useState<AopApprovalDraft | null>(null);
  /** Departments whose submission an edit here withdrew (it could not be kept as submitted) — said once. */
  const [withdrawn, setWithdrawn] = useState<string[] | null>(null);
  const actions = canDecline || canEdit;
  if (!drafts.length) return <p className="text-zinc-500">No 2027 hires planned here.</p>;
  return (
    <div className="overflow-x-auto">
      {declining && <DeclineModal department={department} draft={declining} final={final} onClose={() => setDeclining(null)} />}
      {/* The draft form, in review mode: a submitted department stays submitted with its ticks. */}
      {editing && (
        <AopEditModal
          recordId={editing.recordId}
          label={editing.title}
          root={root}
          inReview
          onWithdrawn={setWithdrawn}
          onClose={() => setEditing(null)}
        />
      )}
      {withdrawn && (
        <p role="status" className="mb-2 flex items-start gap-2 text-amber-700">
          <span className="flex-1">
            Your edit withdrew the submission of {withdrawn.join(' and ')}. It could not be kept as submitted, so it needs
            submitting and reviewing again.
          </span>
          <Button type="button" size="sm" variant="ghost" onClick={() => setWithdrawn(null)}>
            Dismiss
          </Button>
        </p>
      )}
      <table className="w-full min-w-[56rem] border-collapse text-left">
        <thead className="text-xs uppercase tracking-wide text-zinc-500">
          <tr className="border-b border-zinc-200">
            <th className="py-1.5 pr-3 font-medium">Role</th>
            <th className="py-1.5 pr-3 font-medium">Start</th>
            <th className="py-1.5 pr-3 font-medium">Hiring manager</th>
            <th className="py-1.5 pr-3 font-medium">Priority</th>
            <th className="py-1.5 pr-3 text-right font-medium">Per year</th>
            <th className="w-[40%] py-1.5 font-medium">Why this role?</th>
            {actions && (
              <th className="py-1.5 pl-3 font-medium">
                <span className="sr-only">Decision</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {drafts.map((d) => (
            <tr key={d.recordId} className="border-b border-zinc-100 align-top">
              <td className="py-2 pr-3">
                <span className="font-medium text-purple-700">{d.title}</span>
                <span className="block text-xs text-zinc-500">
                  {[d.role, d.trackAndLevel, d.unit].filter(Boolean).join(' · ')}
                  {d.reasonForHiring ? ` · ${d.reasonForHiring}` : ''}
                </span>
              </td>
              <td className="whitespace-nowrap py-2 pr-3">{d.plannedStartDate ? formatDay(d.plannedStartDate) : '—'}</td>
              <td className="py-2 pr-3">{d.hiringManager ?? '—'}</td>
              <td className="py-2 pr-3">{d.priority ?? '—'}</td>
              <td className="py-2 pr-3 text-right">
                {d.yearlyCost != null ? (
                  <span className="whitespace-nowrap">{formatEuro(d.yearlyCost)}</span>
                ) : (
                  <>
                    —{d.costNote && <span className="block max-w-[12rem] text-left text-xs text-zinc-400">{d.costNote}</span>}
                  </>
                )}
              </td>
              <td className="whitespace-pre-line py-2 text-zinc-700">{d.rationale ?? <span className="text-zinc-400">No rationale given.</span>}</td>
              {actions && (
                <td className="py-2 pl-3 text-right">
                  <span className="flex flex-col items-end gap-1.5">
                    {canEdit && (
                      <Button type="button" size="sm" variant="outline" aria-label={`Edit: ${d.title}`} onClick={() => setEditing(d)}>
                        Edit
                      </Button>
                    )}
                    {canDecline && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        aria-label={`${final ? 'Decline' : 'Revisit in next iteration'}: ${d.title}`}
                        onClick={() => setDeclining(d)}
                      >
                        {final ? 'Decline' : 'Revisit in next iteration'}
                      </Button>
                    )}
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Set one role aside for the next iteration (or decline it, in the last one), with a required
 * note; the proxy's refusal is shown word for word. */
function DeclineModal({
  department,
  draft,
  final,
  onClose,
}: {
  department: string;
  draft: AopApprovalDraft;
  final: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const decline = useMutation({
    mutationFn: () => declineAopDraft(department, draft.recordId, note.trim()),
    // The timeline key covers the approvals list too: status and drafts refresh together.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AOP_TIMELINE_QUERY_KEY });
      onClose();
    },
  });
  const valid = note.trim().length > 0 && note.trim().length <= MAX_NOTE;
  return (
    <Modal open onOpenChange={(open: boolean) => !open && !decline.isPending && onClose()}>
      <ModalContent className="!h-max !w-[min(100vw-2rem,30rem)] gap-4 rounded p-6">
        <ModalHeader>
          <ModalTitle className="!text-lg !font-semibold">
            {final ? `Decline ${draft.title}?` : `Revisit ${draft.title} in the next iteration?`}
          </ModalTitle>
        </ModalHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !decline.isPending) decline.mutate();
          }}
        >
          <p className="text-sm text-zinc-600">
            The role leaves the {department} plan for this iteration. The rest stays submitted with its reviews, so it can be
            approved without this one.{' '}
            {final
              ? 'The role stays declined with your note under the table; Anna or David can still restore it.'
              : 'It comes back to the plan when the next iteration opens, with your note for the head to rework it.'}
          </p>
          <Textarea
            label={final ? 'Why is it declined?' : 'Why revisit it later?'}
            rows={3}
            value={note}
            maxLength={MAX_NOTE}
            onChange={(event) => setNote((event.target as HTMLTextAreaElement).value)}
          />
          {decline.error && (
            <p role="alert" className="text-sm text-red-700">
              {(decline.error as Error).message}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="outline" disabled={decline.isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!valid} loading={decline.isPending}>
              {final ? 'Decline role' : 'Revisit in next iteration'}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
}
